import React, { useCallback, useEffect, useRef, useState } from "react";
import { NodeStructureNameInputMaxWidth } from "@src/components/atoms/node-structure-name-input-max-width/node-structure-name-input-max-width";
import { RootState } from "@src/reducers/store";
import { useDispatch, useSelector } from "react-redux";
import { ToolTip } from "@src/components/atoms/tooltip/tooltip";
import { SubTitle } from "@src/components/atoms/sub-title/sub-title";
import styles from "./machine-structure-panel.module.scss";
import { Button } from "@src/components/atoms/buttons/button/button";
import {
  errorSnackbar,
  warningSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { saveEditStructurePageTransferData } from "@src/cloud-connect/node-manager/node-manager-repository";
import {
  updateActiveMachineStructureNode,
  updateActiveViewUnsavedStructureForNodeManagerNavigationModal,
  updateEditStructurePageInitialData,
  updateShowUnsavedStructureForNodeManagerNavigationModal,
  updateStructureWasSaved,
} from "@src/reducers/your-reducer";
import { getMachineStructureNodeset } from "@src/cloud-connect/dependency-injection-container/get-machine-structure-nodeset";
import { useClickOutsideListener } from "@src/hooks/use-click-outside-listener";
import { useI18N } from "@src/language/language-context";
import { queryClientByIdUseCase } from "@src/cloud-connect/client-configuration/use-cases/query-client-by-id-use-case";
import { getNodeManager } from "@src/cloud-connect/dependency-injection-container/get-node-manager";

export const useMachineStructurePanelHook = () => {
  const dispatch = useDispatch();
  const i18n = useI18N();
  const texts = {
    subTitle: i18n("modules.machineStructurePanel.subtitle"),
    warningNoEmptyNodeName: i18n(
      "modules.machineStructurePanel.warningNoEmptyStructureName",
    ),
    lastReceivedAt: i18n("modules.machineStructurePanel.lastReceivedAt"),
    errorFetchingLastUpdatedAt: i18n(
      "modules.machineStructurePanel.errorFetchingLastUpdatedAt",
    ),
    noTimestampAvailable: i18n(
      "modules.machineStructurePanel.noTimestampAvailable",
    ),
    refreshButtonText: i18n("modules.machineStructurePanel.refreshButtonText"),
    errorRefreshingTwinStructure: i18n(
      "modules.machineStructurePanel.errorRefreshingTwinStructure",
    ),
  };

  const initialNodeData = useSelector(
    (state: RootState) => state.yourSlice.editStructurePageInitialData,
  );

  const transferedNodeData = useSelector(
    (state: RootState) => state.yourSlice.editStructurePageTransferData,
  );

  const structureWasSaved = useSelector(
    (state: RootState) => state.yourSlice.structureWasSaved,
  );

  const showUnsavedStructureForNodeManagerNavigationModal = useSelector(
    (state: RootState) =>
      state.yourSlice.unsavedStructureForNodeManagerNavigationModal
        .showUnsavedStructureForNodeManagerNavigationModal,
  );

  const [nodeStructureNameValue, setNodeStructureNameValue] =
    useState("Untitled");

  const [isInputActive, setIsInputActive] = useState(false);
  const [subTitleWidth, setSubTitleWidth] = useState(0);
  const [loadingLastUpdatedAt, setLoadingLastUpdatedAt] = useState(false);
  const [refreshingTwinStructure, setRefreshingTwinStructure] = useState(false);
  const [isRefreshPending, setIsRefreshPending] = useState(false);
  const subTitleWrapperRef = useRef<HTMLDivElement>();
  const saveInputAndButtonWrapperRef = useRef<HTMLDivElement>();
  const refreshAbortControllerRef = useRef<AbortController | null>(null);
  const clientId = useSelector(
    (state: RootState) =>
      state.yourSlice.connectionView.source.connected_via_client,
  );
  const activeMachineStructureNode = useSelector(
    (state: RootState) => state.yourSlice.activeMachineStructureNode,
  );
  const nodeManager = getNodeManager();
  const isDetailsPanelFromTwinStructure = Boolean(activeMachineStructureNode);

  const handleEditNodeStructureName = () => {
    setIsInputActive(true);
  };

  const handleSaveNodeStructureName = (inputValue) => {
    if (inputValue.trim() === "") {
      warningSnackbar(texts.warningNoEmptyNodeName);
    } else {
      setIsInputActive(false);
      saveEditStructurePageTransferData({
        ...transferedNodeData,
        nodeset: {
          ...transferedNodeData.nodeset,
          name: inputValue,
        },
      });
      dispatch(
        updateEditStructurePageInitialData({
          ...initialNodeData,
          nodeset: {
            ...initialNodeData.nodeset,
            name: inputValue,
          },
        }),
      );
      dispatch(updateStructureWasSaved(false));
    }
  };

  const machineStructureNodeset = getMachineStructureNodeset();
  const [machineStructureFlatMapSize, setMachineStructureFlatMapSize] =
    useState(0);
  const [lastUpdatedAt, setLastUpdatedAt] = useState("");

  useEffect(() => {
    const countMachineStructureNodesetMapEntries = () => {
      const count = getMachineStructureNodeset().getFlatMap()
        ? getMachineStructureNodeset().getFlatMap().size
        : 0;
      setMachineStructureFlatMapSize(count);
    };

    countMachineStructureNodesetMapEntries();

    machineStructureNodeset.addObserver(countMachineStructureNodesetMapEntries);

    return () => {
      machineStructureNodeset.removeObserver(
        countMachineStructureNodesetMapEntries,
      );
    };
  }, [machineStructureNodeset]);

  useEffect(() => {
    if (transferedNodeData) {
      setNodeStructureNameValue(
        transferedNodeData.nodeset && transferedNodeData.nodeset.name
          ? transferedNodeData.nodeset.name
          : "Untitled",
      );
    }
  }, [transferedNodeData]);

  useEffect(() => {
    if (subTitleWrapperRef.current) {
      setSubTitleWidth(subTitleWrapperRef.current.offsetWidth);
    }
  }, [isInputActive]);

  const getLastUpdatedAt = useCallback(
    async (signal?: AbortSignal, options?: { showErrorSnackbar?: boolean }) => {
      if (!clientId) return false;

      const shouldShowErrorSnackbar = options?.showErrorSnackbar ?? true;

      setLastUpdatedAt("");
      setLoadingLastUpdatedAt(true);
      try {
        const response = await queryClientByIdUseCase(
          clientId,
          false,
          false,
          signal,
        );
        if (signal?.aborted) return false;

        if (response.success && response.data) {
          setLastUpdatedAt(response?.data?.nodeset_last_updated_at || "");
          return true;
        }

        setLastUpdatedAt("");
        if (shouldShowErrorSnackbar) {
          errorSnackbar(texts.errorFetchingLastUpdatedAt, 5000);
        }
        return false;
      } catch (error) {
        if (signal?.aborted) return false;
        console.error("Error fetching last updated at:", error);
        if (shouldShowErrorSnackbar) {
          errorSnackbar(texts.errorFetchingLastUpdatedAt, 5000);
        }
        return false;
      } finally {
        if (!signal?.aborted) {
          setLoadingLastUpdatedAt(false);
        }
      }
    },
    [clientId, texts.errorFetchingLastUpdatedAt],
  );

  useEffect(() => {
    if (!clientId) return;

    const controller = new AbortController();
    const signal = controller.signal;

    getLastUpdatedAt(signal);

    return () => {
      controller.abort();
    };
  }, [clientId, getLastUpdatedAt]);

  const performRefreshTwinStructure = useCallback(async () => {
    if (refreshingTwinStructure) return;

    refreshAbortControllerRef.current?.abort();
    const controller = new AbortController();
    refreshAbortControllerRef.current = controller;

    setRefreshingTwinStructure(true);
    try {
      if (isDetailsPanelFromTwinStructure) {
        nodeManager.closeNodeManagerDetailsPanel();
      }

      const [machineStructureResult, lastUpdatedResult] =
        await Promise.allSettled([
          nodeManager.loadMachineStructureAndDashboards(controller.signal),
          getLastUpdatedAt(controller.signal, { showErrorSnackbar: false }),
        ]);

      if (controller.signal.aborted) return;

      if (machineStructureResult.status === "rejected") {
        console.error(
          "Error refreshing twin structure:",
          machineStructureResult.reason,
        );
        errorSnackbar(texts.errorRefreshingTwinStructure, 5000);
      }

      if (
        lastUpdatedResult.status === "rejected" ||
        (lastUpdatedResult.status === "fulfilled" && !lastUpdatedResult.value)
      ) {
        errorSnackbar(texts.errorFetchingLastUpdatedAt, 5000);
      }

      dispatch(updateActiveMachineStructureNode(""));
    } finally {
      if (!controller.signal.aborted) {
        setRefreshingTwinStructure(false);
      }
    }
  }, [
    dispatch,
    getLastUpdatedAt,
    refreshingTwinStructure,
    isDetailsPanelFromTwinStructure,
    nodeManager,
  ]);

  const refreshTwinStructure = useCallback(() => {
    const machineStructureIsEmptyOrDefault =
      getMachineStructureNodeset().getFlatMap().size <= 1;

    if (structureWasSaved || machineStructureIsEmptyOrDefault) {
      void performRefreshTwinStructure();
      return;
    }

    setIsRefreshPending(true);
    dispatch(
      updateActiveViewUnsavedStructureForNodeManagerNavigationModal(
        "treeStructure",
      ),
    );
    dispatch(updateShowUnsavedStructureForNodeManagerNavigationModal(true));
  }, [dispatch, performRefreshTwinStructure, structureWasSaved]);

  useEffect(() => {
    if (!isRefreshPending) return;
    if (showUnsavedStructureForNodeManagerNavigationModal) return;

    if (structureWasSaved) {
      setIsRefreshPending(false);
      void performRefreshTwinStructure();
      return;
    }

    setIsRefreshPending(false);
  }, [
    performRefreshTwinStructure,
    isRefreshPending,
    showUnsavedStructureForNodeManagerNavigationModal,
    structureWasSaved,
  ]);

  useEffect(() => {
    return () => {
      refreshAbortControllerRef.current?.abort();
    };
  }, []);

  useClickOutsideListener(
    saveInputAndButtonWrapperRef,
    () => handleSaveNodeStructureName(transferedNodeData.nodeset.name),
    isInputActive,
  );

  return {
    machineStructureFlatMapSize,
    texts,
    lastUpdatedAt,
    loadingLastUpdatedAt,
    refreshingTwinStructure,
    refreshTwinStructure,
  };
};
